import { observer } from "mobx-react-lite";
import { useState } from "react";

import { NoDataTile, Pivot, PivotItem, Spinner, Stack } from "@bps/fluent-ui";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

import { ConditionsList } from "./ConditionsList.tsx";

interface ContactCardProps {
  patientId: string;
}

interface ContactCardBaseProps {
  episodesOfCare: EpisodeOfCare[];
}

enum ConditionKey {
  claim = "claim",
  private = "private"
}

const ConditionsCardBase: React.FC<ContactCardBaseProps> = ({
  episodesOfCare
}) => {
  const episodesOfCareWithClaims = episodesOfCare.filter(x => !x.isPrivate);
  const privateEpisodesOfCare = episodesOfCare.filter(x => !!x.isPrivate);

  const privateOnly =
    !episodesOfCareWithClaims.length && !!privateEpisodesOfCare.length;

  const [conditionKey, setConditionKey] = useState<string>(
    privateOnly ? ConditionKey.private : ConditionKey.claim
  );

  const getNoDataTile = (text: string) => (
    <NoDataTile
      textProps={{ text }}
      linkProps={{ hidden: true }}
      showBoxShadow={false}
      styles={{ root: { minHeight: 85 } }}
      greyView={true}
    />
  );

  if (!episodesOfCare.length) {
    return getNoDataTile("No conditions");
  }

  const claimPivotItem = (
    <PivotItem
      itemCount={episodesOfCareWithClaims.length}
      headerText="ACC"
      itemKey={ConditionKey.claim}
      key={ConditionKey.claim}
    >
      {!!episodesOfCareWithClaims.length ? (
        <ConditionsList episodesOfCare={episodesOfCareWithClaims} />
      ) : (
        getNoDataTile("No ACC claims")
      )}
    </PivotItem>
  );

  const privatePivotItem = (
    <PivotItem
      itemCount={privateEpisodesOfCare.length}
      headerText="Private"
      itemKey={ConditionKey.private}
      key={ConditionKey.private}
    >
      {!!privateEpisodesOfCare.length ? (
        <ConditionsList
          hideClaimNumberColumn
          episodesOfCare={privateEpisodesOfCare}
        />
      ) : (
        getNoDataTile("No conditions")
      )}
    </PivotItem>
  );

  return (
    <Pivot
      styles={{ itemContainer: { marginTop: 8 } }}
      selectedKey={conditionKey}
      onLinkClick={(item: PivotItem) => {
        setConditionKey(item.props.itemKey!);
      }}
    >
      {claimPivotItem}
      {privatePivotItem}
    </Pivot>
  );
};

export const ConditionsCard: React.FC<ContactCardProps> = observer(
  ({ patientId }) => {
    const { acc } = useStores();
    return (
      <Stack styles={{ root: { marginTop: 16, padding: "0 10px 16px" } }}>
        <DataFetcher<EpisodeOfCare[]>
          refetchId={acc.ui.recentlyUpdatedClaimKey}
          fetch={async ({ clinical }) => {
            return await clinical.getPatientEpisodesOfCare(patientId, {
              loadCalendarEvents: true,
              loadClaims: true
            });
          }}
          fallback={<Spinner />}
        >
          {episodesOfCare => (
            <ConditionsCardBase episodesOfCare={episodesOfCare} />
          )}
        </DataFetcher>
      </Stack>
    );
  }
);
